import { useEffect, useMemo, useState } from 'react';
import { sortHand, type HeartsLegal, type HeartsView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { seatPosition, SeatView, MySeat, useNarrow, useSpeech } from './common';

const OFFSETS: Record<string, { x: number; y: number }> = {
  bottom: { x: 0, y: 34 },
  left: { x: -62, y: 0 },
  top: { x: 0, y: -34 },
  right: { x: 62, y: 0 },
};

export function HeartsTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as HeartsView;
  const legal = useGame((s) => s.legal) as HeartsLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const speech = useSpeech();
  const [selected, setSelected] = useState<string | null>(null);
  const [toPass, setToPass] = useState<string[]>([]);
  const [sent, setSent] = useState(false);
  const hand = useMemo(() => sortHand(view.hand), [view.hand]);
  const playable = new Set(legal?.kind === 'play' ? legal.cards : []);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const passing = view.phase === 'PASSING';
  const iMustPass = !!myTurn && legal?.kind === 'pass';
  const narrow = useNarrow();
  const trickSize = narrow ? 'md' : 'lg';
  const k = narrow ? 0.7 : 1;
  const dirLabel = { left: t('hr.dir.left'), right: t('hr.dir.right'), across: t('hr.dir.across'), hold: t('hr.dir.hold') }[view.passDirection];

  // A fresh deal (or a failed send) always starts with nothing selected.
  useEffect(() => {
    setToPass([]);
    setSent(false);
  }, [view.round, view.phase]);

  const onCard = (c: string) => {
    if (iMustPass) {
      setToPass((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : cur.length < 3 ? [...cur, c] : cur));
      return;
    }
    if (!myTurn || legal?.kind !== 'play' || !playable.has(c)) return;
    setSelected(c);
    void act({ type: 'play', card: c }).finally(() => setSelected(null));
  };

  const confirmPass = () => {
    if (toPass.length !== 3 || sent) return;
    setSent(true);
    void act({ type: 'pass', cards: toPass }).catch(() => setSent(false));
  };

  const showReceived = view.phase !== 'PASSING' && view.tricks.length === 0 && view.received.length > 0 && view.passDirection !== 'hold';

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2, 3].map((s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={4}
            turn={turn}
            speech={speech[s]}
            sub={<span className="dim num">{view.totals[s] ?? 0} pts</span>}
            tags={
              <>
                {view.dealer === s && <span className="tag">D</span>}
                {passing && view.passed[s] && <span className="tag seen">{t('hr.passed')}</span>}
                {!passing && (view.pointsTaken[s] ?? 0) > 0 && <span className="tag blind">♥ {view.pointsTaken[s]}</span>}
              </>
            }
          />
        ))}
        <div className="felt-center centered">
          <div className="center-note">
            {t('sp.round', { n: view.round })}
            {view.heartsBroken && ' · ♥ ' + t('hr.broken')}
          </div>
          {passing && (
            <div className="center-note" role="status">
              {view.passDirection === 'hold' ? t('hr.noPass') : t('hr.passTitle', { dir: dirLabel })}
              {!iMustPass && turn?.seats[0] !== undefined && ` · ${t('table.waitingFor', { name: name(turn.seats[0]) })}`}
            </div>
          )}
          <div className={`trick ${view.phase === 'TRICK_END' ? 'collect' : ''}`} key={`trick-${view.round}-${view.tricks.length}-${view.phase === 'TRICK_END'}`} aria-label="Cards on the table">
            {view.trick.map((p, i) => {
              const dir = seatPosition(p.seat, mySeat, 4).dir;
              const o = OFFSETS[dir]!;
              const off = { x: o.x * k, y: o.y * k };
              const w = view.phase === 'TRICK_END' && view.lastTrick ? OFFSETS[seatPosition(view.lastTrick.winner, mySeat, 4).dir]! : null;
              const winnerDir = w ? { x: w.x * k, y: w.y * k } : null;
              return (
                <div
                  key={p.card}
                  className="slot fly"
                  style={{
                    transform: `translate(calc(-50% + ${off.x}px), calc(-50% + ${off.y}px)) rotate(${(i - 1.5) * 4}deg)`,
                    ['--from-x' as string]: `${off.x * 3}px`,
                    ['--from-y' as string]: `${off.y * 4}px`,
                    ...(winnerDir ? { ['--to-x' as string]: `${winnerDir.x * 3}px`, ['--to-y' as string]: `${winnerDir.y * 4}px`, animationDelay: '0.45s' } : {}),
                    zIndex: i,
                  }}
                >
                  <PlayingCard id={p.card} size={trickSize} />
                </div>
              );
            })}
          </div>
          {view.phase === 'ROUND_END' && view.roundResults.length > 0 && (
            <div className="card glass" style={{ textAlign: 'center' }}>
              <strong>{t('sp.roundScores', { n: view.round })}</strong>
              {view.roundResults.at(-1)!.moon !== null && <div>🌙 {t('hr.moon', { name: name(view.roundResults.at(-1)!.moon!) })}</div>}
              <div className="row" style={{ justifyContent: 'center', marginTop: 6 }}>
                {view.roundResults.at(-1)!.scores.map((sc, s) => (
                  <span key={s} className={`pill ${sc === 0 ? 'accent' : ''}`}>
                    {name(s)}: {sc}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={mySeat !== null ? <>{view.totals[mySeat] ?? 0} pts{!passing && (view.pointsTaken[mySeat] ?? 0) > 0 ? ` · ♥ ${view.pointsTaken[mySeat]}` : ''}</> : null} />
      </div>
      <div className="table-bottom">
        {iMustPass && (
          <div className="bid-picker" role="group" aria-label={t('hr.passTitle', { dir: dirLabel })}>
            <div className="bid-head">
              <strong>{t('hr.passTitle', { dir: dirLabel })}</strong>
              <span className="dim">{t('hr.pickThree', { n: toPass.length })}</span>
            </div>
            <button className="btn primary block bid-confirm" disabled={toPass.length !== 3 || sent} onClick={confirmPass}>
              {t('hr.passBtn')}
            </button>
          </div>
        )}
        {showReceived && (
          <div className="row" style={{ justifyContent: 'center', gap: 6, alignItems: 'center' }} aria-label={t('hr.received')}>
            <span className="dim">{t('hr.received')}</span>
            {view.received.map((c) => (
              <PlayingCard key={c} id={c} size="sm" />
            ))}
          </div>
        )}
        <Hand size="lg" label="Your hand">
          {hand.map((c, i) => (
            <PlayingCard
              key={c}
              id={c}
              size="lg"
              dealIndex={passing ? i : undefined}
              selected={selected === c || toPass.includes(c)}
              playable={myTurn && (iMustPass || playable.has(c))}
              unplayable={myTurn && legal?.kind === 'play' && !playable.has(c)}
              onClick={() => onCard(c)}
            />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              {[0, 1, 2, 3].map((s) => (
                <th key={s} className="ellipsis" style={{ maxWidth: 70 }}>{name(s)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.roundResults.map((row, r) => (
              <tr key={r}>
                <td>R{r + 1}{row.moon !== null ? ' 🌙' : ''}</td>
                {row.scores.map((v, s) => (
                  <td key={s} className="num" style={{ color: row.moon === s ? 'var(--accent)' : undefined }}>{v}</td>
                ))}
              </tr>
            ))}
            <tr>
              <td><strong>Total</strong></td>
              {view.totals.map((v, s) => (
                <td key={s} className="num"><strong>{v}</strong></td>
              ))}
            </tr>
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('hr.scoreHint', { target: view.config.targetScore })}</p>
      </Modal>
    </>
  );
}
